
import { useState, useCallback, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { LIST_NAMES, type MovieListName } from "@/hooks/search/types/listConstants";

// Define tab order for direction tracking
const TAB_ORDER = [LIST_NAMES.FAVORITES, LIST_NAMES.UNSEEN_GEMS, LIST_NAMES.WATCHLIST];

/**
 * Custom hook to manage tab navigation with transition animations and URL persistence
 */
export const useTabNavigation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize currentTab from URL or default to favorites with proper typing
  const urlTab = searchParams.get('tab');
  const initialTab: MovieListName = (urlTab && Object.values(LIST_NAMES).includes(urlTab as MovieListName))
    ? urlTab as MovieListName
    : LIST_NAMES.FAVORITES;
  
  const [currentTab, setCurrentTabState] = useState<MovieListName>(initialTab);
  const [transitionDirection, setTransitionDirection] = useState<"left" | "right" | null>(null);
  const prevTabRef = useRef<MovieListName>(currentTab);
  
  // DEBUG: Log tab changes
  console.log(`🔍 useTabNavigation current state:`, {
    currentTab,
    urlTab,
    initialTab,
    LIST_NAMES_VALUES: Object.values(LIST_NAMES)
  });
  
  // FIXED: Remove dependency on currentTab to prevent infinite loops
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    const validTab: MovieListName = (urlTab && Object.values(LIST_NAMES).includes(urlTab as MovieListName))
      ? urlTab as MovieListName
      : LIST_NAMES.FAVORITES;
    
    console.log(`🔍 Tab sync effect:`, {
      urlTab,
      validTab,
      currentTab,
      needsUpdate: validTab !== currentTab
    });
    
    if (validTab !== currentTab) {
      setCurrentTabState(validTab);
      prevTabRef.current = validTab;
    }
  }, [searchParams]); // Only depend on searchParams, not currentTab
  
  // Enhanced tab change handler with direction tracking and URL updates
  const handleTabChange = useCallback((value: MovieListName) => {
    console.log(`🔍 handleTabChange called:`, {
      from: prevTabRef.current,
      to: value,
      LIST_NAMES_WATCHLIST: LIST_NAMES.WATCHLIST
    });
    
    // Determine direction based on tab order
    const prevIndex = TAB_ORDER.indexOf(prevTabRef.current);
    const newIndex = TAB_ORDER.indexOf(value);
    
    if (prevIndex < newIndex) {
      setTransitionDirection("right");
    } else if (prevIndex > newIndex) {
      setTransitionDirection("left");
    } else {
      setTransitionDirection(null);
    }
    
    // Update state
    setCurrentTabState(value);
    prevTabRef.current = value;
    
    // Update URL with new tab
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === LIST_NAMES.FAVORITES) {
      // Remove tab param for default value to keep URL clean
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', value);
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);
  
  return {
    currentTab,
    transitionDirection,
    handleTabChange
  };
};
